import React, {useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {useSearchParams} from 'react-router-dom';
// import ReactJson from 'react-json-view';
import {JsonViewer} from '@textea/json-viewer';
import {useAccountContext} from '../../../contexts/AccountContext';
import useOVRecommendDetails from '../../../hooks/useOVRecommendDetails';
import {useQueryParams} from '../../../hooks/useQueryParams';
import Loading from '../../../components/Loading/Loading';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {
  CreateReccProps,
  CreateRecommendContext,
  RecommendEditorForm,
  createReccContext,
  useRecommendContext,
} from './CreateRecommendHelpers';
import useTitle from '../../../hooks/useTitle';
import {ErrorPage} from '../../../pages/ErrorPage';
import {mainPlanTypeType, recommendFieldsType} from './recommendFields';
import {recommendValidation} from './recommendValidation';
import OWNERSHIP from '../../../constants/Ownership';
import {OVRecommendActionEnum, BusinessUnitEnum} from '../../../schema-types/globalTypes';
import {FormReadOnly} from '../../../components/AutoForm/AutoFormNew';
import {
  RECOMMENDATION_TYPES,
  SPP_RECOMMENDATION_TYPES,
} from '../../../constants/SolutionType';
import {getCuroURL} from '../../../utils/getCuroURL';
import {useDjangoContext} from '../../../contexts/useAuthContext';
import RecommendsTable from '../../../components/RecommendsTable/RecommendsTable';
import saltus from '../../../shared/logger';
import {SaltusAny} from '../../../utils/SaltusAny';
import validateUUID from '../../../utils/validateUUID';

export const CreateRecommendInner: React.FC = () => {
  const {account, accountError, accountLoading} = useAccountContext();
  const {djangoUser} = useDjangoContext();
  const {reviewDetails} = useReviewContext();
  const {isSPPDA: isSppDa, loadingOvRecommend, recommendDetails} = useRecommendContext();
  const [searchParams, setSearchParams] = useSearchParams();
  useTitle(`New requirement - ${account?.name}`);
  useEffect(() => {
    if (recommendDetails) {
      saltus.log({'Loaded recommend values': recommendDetails});
    }
  }, [recommendDetails]);

  if (accountError) {
    return (
      <ErrorPage
        // retry={accountsRetry}
        code="500"
        text="We were unable to retrieve the account information."
      />
    );
  }

  if (accountLoading || loadingOvRecommend) {
    return (
      <Box style={{marginTop: '10rem'}}>
        <Loading entityName="account" />
      </Box>
    );
  }

  if (!account) {
    return <ErrorPage code="404" text="Account not found" />;
  }

  if (!reviewDetails) {
    return <ErrorPage code="404" text="Review not found" />;
  }

  const initialValueOverrides: Partial<recommendFieldsType> = {
    note: recommendDetails?.note || undefined,
    how: 'Recommendation',
    contact: '782d5c26-768a-4be9-ab57-ae4fbfcc535a', // jon
    // ownership: OWNERSHIP.SINGLE_LIFE,
    planAction: OVRecommendActionEnum.SET_UP_NEW,
    mainPlanType: 'gia',
    goal: {
      __typename: 'GoalType',
      id: '9dbbb584-cc5e-4727-bbf6-836141697e43',
      name: 'Buy the children a flat',
      ownership: 'Account owned',
      contact: null,
    },
  };
  const debugServerValue = JSON.stringify(recommendDetails || null, null, 2);

  return (
    <RecommendEditorForm
      recommendAccount={account}
      debugChildrenValue={debugServerValue}
      debugChildren={
        <>
          <RecommendsTable recommends={recommendDetails?.recommends || []} />
          {/* <ReactJson
            src={recommendDetails || {}}
            name={false}
            shouldCollapse={(x) => {
              if (x.name && ['goal', 'review', 'contact', 'account'].includes(x.name)) {
                return true;
              }
              return false;
            }}
            collapsed={process.env.NODE_ENV === 'development' ? 3 : 1}
          /> */}
          <JsonViewer
            value={recommendDetails || {}}
            editable={false}
            defaultInspectControl={(path: SaltusAny, val: SaltusAny) => {
              if (
                ['goals', 'review'].some((item) => {
                  return item === path[0];
                })
              ) {
                return false;
              }
              return true;
            }}
            // theme={theme}
            // indentWidth={indent}
            // highlightUpdates={true}
            // onChange={handleChange}
            // keyRenderer={KeyRenderer}
            // valueTypes={[imageType]}
          />
          {recommendDetails?.recommends?.map((r) => (
            <a
              key={r.curoId}
              href={getCuroURL(djangoUser, 'recommendation', String(r.curoId))}
            >
              {r.curoId}: Failed: {r.isFailedSubmission}
            </a>
          ))}
          <div className="recommends-list">
            {recommendDetails?.recommends?.map((x) => {
              const text = `'${x.advice}',`;
              return (
                <div key={x.id} className="recommend">
                  {text}
                </div>
              );
            })}
          </div>
        </>
      }
      initialValueOverrides={
        process.env.NODE === 'development'
          ? initialValueOverrides
          : {
              isSppDa: isSppDa || false,
              planAction:
                (searchParams.get('planAction') as OVRecommendActionEnum) || undefined,
              mainPlanType:
                (searchParams.get('mainPlanType') as mainPlanTypeType) || undefined,
              how:
                (searchParams.get('how') as keyof typeof RECOMMENDATION_TYPES) ||
                undefined,
              contact: searchParams.get('contact') || undefined,
              sppType:
                (searchParams.get('sppType') as keyof typeof SPP_RECOMMENDATION_TYPES) ||
                undefined,
              investorCategory: reviewDetails?.account?.investorCategory || undefined,
            }
      }
      submit={() => {}}
      formFields={[]}
      validate={(values) => {
        const errors = recommendValidation(values);
        return errors;
      }}
    />
  );
};

function CreateRecommend({isSppDA: isSPPDA}: CreateReccProps) {
  const {account} = useAccountContext();
  const {reviewId, reviewDetails} = useReviewContext();
  useTitle(`Create requirement - ${reviewDetails?.name}`);
  const {queryGetOVRecommendationDetails, ovRecommendationData, loadingOvRecommend} =
    useOVRecommendDetails();
  const [ovRecommendId, setOvRecommendId] = useState<null | string>(null);
  const query = useQueryParams();

  useEffect(() => {
    // uses layout effect instead of useEffect to make sure there isnt a render of a previously viewed recc
    const queryOvrecommendId = query.get('ovrecommendId');
    if (!queryOvrecommendId) {
      return;
    }
    setOvRecommendId(queryOvrecommendId);
  }, [query]);

  useEffect(() => {
    // uses layout effect instead of useEffect to make sure there isnt a render of a previously viewed recc
    if (!ovRecommendId) {
      return;
    }
    queryGetOVRecommendationDetails({
      variables: {
        ovrecommendId: validateUUID(ovRecommendId)!,
      },
      fetchPolicy: 'network-only',
    });
  }, [ovRecommendId]);

  const contextVal: createReccContext = useMemo(() => {
    return {
      isSPPDA:
        isSPPDA ||
        account?.businessUnit?.partnershipType === BusinessUnitEnum.DA ||
        false,
      isSPPAR:
        isSPPDA ||
        account?.businessUnit?.partnershipType === BusinessUnitEnum.AR ||
        false,
      loadingOvRecommend: loadingOvRecommend && !!ovRecommendId, // only loading if its actually loading and there's an id associated
      recommendDetails: ovRecommendationData || null,
      recommendReviewId: ovRecommendationData?.review?.id || reviewId, // if theres already an associated reviewId, use that
      ovRecommendId: ovRecommendId!,
      setOvRecommendId,
    };
  }, [
    account?.businessUnit?.partnershipType,
    loadingOvRecommend,
    ovRecommendId,
    ovRecommendationData,
    ovRecommendId,
    setOvRecommendId,
  ]);

  if (!ovRecommendationData && ovRecommendId) {
    return <Loading entityName={isSPPDA ? 'plan' : 'recommendation'} />;
  }

  return (
    <CreateRecommendContext.Provider value={contextVal}>
      <ShowIfDebugWrapper debugKey="forms">
        Account: {account?.name}. Account curo id: {account?.curoId}. Review id:{' '}
        {reviewId}. Review name: {reviewDetails?.name || ''}
      </ShowIfDebugWrapper>

      <FormReadOnly
        readOnly={ovRecommendationData?.recommends?.some((rec) => rec.curoId) || false}
      >
        <CreateRecommendInner />
        <Box mt={3} />
      </FormReadOnly>
    </CreateRecommendContext.Provider>
  );
}

export default CreateRecommend;
